import { useMemo } from "react";
import { GridColDef } from "@mui/x-data-grid-premium";
import { Box, Chip } from "@mui/material";
import {
  Business,
  CheckCircle,
  Event,
  Groups,
  HowToReg,
  Person,
  PriorityHigh
} from "@mui/icons-material";
import {
  dateColumn,
  normalizeProgress,
  normalizeSignOff,
  operationalReadinessColumn,
  renderPriorityCell,
  renderProgressCell,
  renderSignOffCell,
  SIGN_OFF_ORDER,
  SignOffLabel
} from "../../../../components/datagrid/overviewCells";
import { MergerRow } from "../../dataHooks/mergerQueries";

const KIND_LABELS = { counterparty: "Counterparty", broker: "Broker" };

const progressColumn = (
  key: keyof MergerRow["progress"],
  headerName: string,
  brokersHaveIt = true
): GridColDef<MergerRow> => ({
  field: `progress_${key}`,
  headerName,
  width: 150,
  sortable: true,
  filterable: true,
  valueGetter: (_value, row) =>
    row.kind === "broker" && !brokersHaveIt
      ? null
      : normalizeProgress(row.progress[key]),
  // A broker has no Treasury or Tech lifecycle, so the cell stays empty rather than reading
  // "Not Initiated", which would suggest work that will never exist.
  renderCell: (params) =>
    params.value ? renderProgressCell(params.value) : null
});

/**
 * Like the TC and Brokers overviews, minus Market Access (a merger has none) and the MA Handler
 * column (so there is no handler), plus a Type column since both books share the grid.
 */
export const useMergerColumns = (
  variant: "active" | "completed"
): GridColDef<MergerRow>[] =>
  useMemo(() => {
    const columns: GridColDef<MergerRow>[] = [
      {
        field: "name",
        headerName: "Name",
        width: 300,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Business fontSize="small" />
            Name
          </Box>
        )
      },
      {
        field: "kind",
        headerName: "Type",
        width: 140,
        type: "singleSelect",
        valueOptions: Object.values(KIND_LABELS),
        valueGetter: (_value, row) => KIND_LABELS[row.kind],
        renderCell: (params) => (
          <Chip size="small" variant="outlined" label={params.value} />
        )
      },
      {
        field: "tradingTeam",
        headerName: "Trading Team",
        width: 200,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Groups fontSize="small" />
            Trading Team
          </Box>
        )
      },
      {
        field: "contactPersonTradingTeam",
        headerName: "MFT Contact Person",
        width: 220,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Person fontSize="small" />
            MFT Contact Person
          </Box>
        )
      },
      // Mergers are never requested, so Created stands in for the other grids' Requested column.
      dateColumn<MergerRow>(
        "createdAt",
        "Created",
        (row) => row.createdAt,
        <Event fontSize="small" />
      )
    ];

    if (variant === "completed") {
      columns.push(
        operationalReadinessColumn<MergerRow>(
          (row) => row.operationalReadinessStatus
        )
      );
      columns.push({
        field: "signOffCompletedAt",
        headerName: "Signed off",
        width: 170,
        type: "date",
        valueGetter: (_value, row) =>
          row.signOffCompletedAt ? new Date(row.signOffCompletedAt) : null,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircle fontSize="small" />
            Signed off
          </Box>
        )
      });
      return columns;
    }

    columns.push(
      {
        field: "urgent",
        headerName: "Priority",
        width: 120,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PriorityHigh fontSize="small" />
            Priority
          </Box>
        ),
        renderCell: (params) => renderPriorityCell(params.value)
      },
      operationalReadinessColumn<MergerRow>(
        (row) => row.operationalReadinessStatus
      ),
      {
        field: "signOffStatus",
        headerName: "Sign Off",
        width: 150,
        type: "singleSelect",
        valueOptions: SIGN_OFF_ORDER,
        valueGetter: (_value, row) => normalizeSignOff(row.signOffStatus),
        sortComparator: (a: SignOffLabel, b: SignOffLabel) =>
          SIGN_OFF_ORDER.indexOf(a) - SIGN_OFF_ORDER.indexOf(b),
        renderCell: (params) => renderSignOffCell(params.value),
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HowToReg fontSize="small" />
            Sign Off
          </Box>
        )
      },
      progressColumn("compliance", "Compliance"),
      progressColumn("legal", "Legal"),
      progressColumn("risk", "Risk"),
      progressColumn("creditRisk", "Credit Risk"),
      progressColumn("treasury", "Treasury", false),
      progressColumn("finance", "Finance"),
      progressColumn("tech", "Tech", false)
    );
    return columns;
  }, [variant]);
